/** No credentials or message content are persisted in browser storage. */
export const PUSH_OWNER_KEY = "formalys.chat.push.owner";
export type PushOwner = { actorId: string; subscriptionId: string };
export const pushSupported = () =>
  typeof Notification !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  window.isSecureContext;
export const readPushOwner = (): PushOwner | null => {
  try {
    return JSON.parse(localStorage.getItem(PUSH_OWNER_KEY) || "null");
  } catch {
    return null;
  }
};
export const writePushOwner = (owner: PushOwner | null) => {
  if (owner) localStorage.setItem(PUSH_OWNER_KEY, JSON.stringify(owner));
  else localStorage.removeItem(PUSH_OWNER_KEY);
};
export async function chatNotificationRequest(
  token: string,
  path: string,
  options: RequestInit = {},
) {
  const response = await fetch(
    new Request(`${process.env.REACT_APP_API_URI?.replace(/\/$/, "")}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }),
  );
  if (!response.ok)
    throw Object.assign(new Error("CHAT_NOTIFICATION_REQUEST_FAILED"), {
      status: response.status,
    });
  return response.json();
}
let pushWork: Promise<unknown> = Promise.resolve();
/** Serialize subscribe/logout/account-switch operations, including late browser permission replies. */
export function withPushLock<T>(work: () => Promise<T>): Promise<T> {
  const locks = (
    navigator as Navigator & {
      locks?: {
        request: <R>(name: string, work: () => Promise<R>) => Promise<R>;
      };
    }
  ).locks;
  const result = pushWork
    .catch(() => {})
    .then(() => (locks ? locks.request("formalys-chat-push", work) : work()));
  pushWork = result.catch(() => {});
  return result;
}
export async function revokeChatPush(actorId: string, token: string) {
  return withPushLock(async () => {
    const owner = readPushOwner();
    if (owner?.actorId !== actorId) return;
    // Stop delivery at the browser even if the HTTP session is already invalid/offline.
    const registration = await navigator.serviceWorker.getRegistration("/");
    const subscription = await registration?.pushManager.getSubscription();
    await subscription?.unsubscribe();
    writePushOwner(null);
    await Promise.all(
      ((await registration?.getNotifications()) ?? []).map((notification) =>
        notification.close(),
      ),
    );
    await chatNotificationRequest(
      token,
      `/chat/push-subscriptions/${owner.subscriptionId}`,
      { method: "DELETE", keepalive: true },
    ).catch(() => {});
  });
}
export function applicationServerKey(value: string): Uint8Array {
  const raw = atob(value.replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (character) => character.charCodeAt(0));
}

export async function claimChatToast(
  actorId: string,
  id: string,
): Promise<boolean> {
  const claim = async () => {
    try {
      const key = `formalys.chat.alerts.${actorId}`;
      const ids: string[] = JSON.parse(localStorage.getItem(key) || "[]");
      if (ids.includes(id)) return false;
      localStorage.setItem(key, JSON.stringify([...ids.slice(-199), id]));
    } catch {
      /* Storage can be disabled; only the focused tab displays alerts. */
    }
    return true;
  };
  const locks = (
    navigator as Navigator & {
      locks?: {
        request: <R>(name: string, work: () => Promise<R>) => Promise<R>;
      };
    }
  ).locks;
  return locks
    ? locks.request(`formalys-chat-alerts-${actorId}`, claim)
    : claim();
}
